import { cn } from '@/lib/cn';

export interface SpinnerProps {
  size?: number;
  /** Announced to screen readers. Set to null for a decorative spinner. */
  label?: string | null;
  className?: string;
}

export function Spinner({ size = 18, label = 'Loading', className }: SpinnerProps) {
  return (
    <span
      role={label ? 'status' : undefined}
      aria-label={label ?? undefined}
      aria-hidden={label ? undefined : true}
      className={cn(
        'inline-block animate-[spin_0.7s_linear_infinite] rounded-full border-2 border-line border-t-accent',
        className,
      )}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * The loading state for a whole screen. A bare centred spinner rather than a
 * skeleton, because a skeleton that guesses the wrong shape reads as a bug in
 * the half-second before the data lands.
 */
export function LoadingScreen({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid min-h-[40dvh] place-items-center gap-3 py-16 text-center">
      <Spinner size={22} label={null} className="mx-auto" />
      <p className="text-ui text-subtle">{label}</p>
    </div>
  );
}

/**
 * Rows of shimmering bars, for lists where the shape IS known - a table with
 * a fixed column count, a board column.
 */
export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-10 rounded-md bg-sunken"
          style={{ opacity: 1 - index * 0.13 }}
        />
      ))}
    </div>
  );
}
